// Ecommerce → Pedidos (/ecommerce/pedidos) + Carritos Abandonados (/ecommerce/carritos).
// Bandeja con la máquina de estados del pedido: aprobar transferencias
// (comprobante visible), despachar con guía, entregar, cancelar/reembolsar.
import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, PackageCheck, ShoppingBasket, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls } from '@/features/admin/shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { ESTADO_PEDIDO_BADGE, PedidoT, ecomRpc, usd } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

interface DetalleT extends PedidoT {
  cliente_nombre: string; cliente_email: string | null; cliente_telefono: string | null;
  cliente_identificacion: string | null; direccion: string | null; ciudad: string | null;
  referencia: string | null; zona_nombre: string | null; guia_envio: string | null;
  comprobante: string | null; subtotal: number; iva: number; envio: number; notas: string | null;
  lineas: { titulo: string; cantidad: number; precio_unitario: number; subtotal: number }[];
  eventos: { evento: string; fecha: string }[];
}

export function PedidosWeb() {
  const qc = useQueryClient();
  const [festado, setFestado] = useState('todos');
  const [abierto, setAbierto] = useState<string | null>(null);
  const [guia, setGuia] = useState('');

  const { data: pedidos = [] } = useQuery<PedidoT[]>({
    queryKey: ['ecom-pedidos', festado],
    queryFn: () => ecomRpc<PedidoT[]>('ecom_pedidos', { _estado: festado, _dias: 60 }),
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 10000,
  });
  const { data: detalle } = useQuery<DetalleT | null>({
    queryKey: ['ecom-pedido-det', abierto],
    queryFn: () => ecomRpc<DetalleT | null>('ecom_pedido_detalle', { _id: abierto }),
    enabled: !!abierto,
    staleTime: 0, refetchOnMount: 'always',
  });

  const avanzar = async (id: string, accion: string, datos: Record<string, unknown> = {}) => {
    try {
      await ecomRpc('ecom_pedido_avanzar', { _id: id, _accion: accion, _datos: datos });
      void qc.invalidateQueries({ queryKey: ['ecom-pedidos'] });
      void qc.invalidateQueries({ queryKey: ['ecom-pedido-det'] });
      toast.success('Pedido actualizado');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const getVal = useCallback((p: PedidoT, k: string): string | number | null => {
    switch (k) {
      case 'codigo': return p.codigo; case 'cliente': return p.cliente;
      case 'total': return Number(p.total); case 'estado': return p.estado;
      case 'pago': return p.pago_metodo; case 'fecha': return p.created_at;
      default: return null;
    }
  }, []);
  const sort = useSort(pedidos, getVal);
  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {dlg.ui}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><PackageCheck className="w-5 h-5" /> Pedidos de la tienda</h1>
        <select className={`${fieldCls} !w-auto`} value={festado} onChange={(e) => setFestado(e.target.value)}>
          <option value="todos">Todos</option>
          <option value="pendiente_pago">Pendientes de pago</option>
          <option value="pagado">Pagados</option>
          <option value="en_preparacion">En preparación</option>
          <option value="enviado">Enviados</option>
          <option value="entregado">Entregados</option>
          <option value="cancelado">Cancelados</option>
        </select>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <SortTh col="codigo" sort={sort}>Pedido</SortTh>
                  <SortTh col="fecha" sort={sort}>Fecha</SortTh>
                  <SortTh col="cliente" sort={sort}>Cliente</SortTh>
                  <SortTh col="pago" sort={sort}>Pago</SortTh>
                  <th className="py-2 pr-3 text-left">Entrega</th>
                  <SortTh col="total" sort={sort} align="right">Total</SortTh>
                  <SortTh col="estado" sort={sort} align="center">Estado</SortTh>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((p, i) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono whitespace-nowrap">{p.codigo}
                      {p.ticket_id && <span title="Facturada" className="ml-1">🧾</span>}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">{new Date(p.created_at).toLocaleString(LOCALE, { hour12: false })}</td>
                    <td className="py-2 pr-3">
                      <div>{p.cliente}</div>
                      <div className="text-xs text-muted-foreground">{p.email ?? p.telefono ?? ''}</div>
                    </td>
                    <td className="py-2 pr-3 capitalize">{p.pago_metodo}
                      {p.pago_metodo === 'transferencia' && p.estado === 'pendiente_pago' && (
                        <div className={`text-xs ${p.tiene_comprobante ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                          {p.tiene_comprobante ? '📎 comprobante recibido' : 'sin comprobante'}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 capitalize">{p.entrega_metodo}{p.zona ? ` · ${p.zona}` : ''}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{usd(p.total)}</td>
                    <td className="py-2 pr-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ESTADO_PEDIDO_BADGE[p.estado] ?? ''}`}>
                        {p.estado.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setAbierto(abierto === p.id ? null : p.id); setGuia(''); }}>
                        {abierto === p.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </Button>
                    </td>
                  </tr>
                ))}
                {!sort.sorted.length && (
                  <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">Sin pedidos en el filtro.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {abierto && detalle && (
            <div className="mt-3 border rounded-md p-3 grid gap-4 md:grid-cols-2 text-sm">
              <div className="space-y-2">
                <div className="font-semibold">{detalle.codigo} — {detalle.cliente_nombre}</div>
                <div className="text-xs text-muted-foreground">
                  {detalle.cliente_email ?? ''} {detalle.cliente_telefono ? `· ${detalle.cliente_telefono}` : ''}
                  {detalle.cliente_identificacion ? ` · ${detalle.cliente_identificacion}` : ' · consumidor final'}
                </div>
                {detalle.entrega_metodo === 'envio' && (
                  <div className="text-xs">
                    <Truck className="w-3.5 h-3.5 inline mr-1" />
                    {detalle.zona_nombre} · {detalle.direccion} {detalle.ciudad ? `· ${detalle.ciudad}` : ''}
                    {detalle.referencia ? ` (${detalle.referencia})` : ''}
                  </div>
                )}
                {detalle.notas && <div className="text-xs">📝 {detalle.notas}</div>}
                <div className="border rounded-md divide-y">
                  {detalle.lineas.map((l, i) => (
                    <div key={i} className="px-2 py-1 flex justify-between">
                      <span>{Number(l.cantidad)}× {l.titulo}</span><span className="font-mono">{usd(l.subtotal)}</span>
                    </div>
                  ))}
                  <div className="px-2 py-1 flex justify-between text-xs text-muted-foreground">
                    <span>Subtotal {usd(detalle.subtotal)} · IVA {usd(detalle.iva)} · Envío {usd(detalle.envio)}</span>
                    <span className="font-semibold text-foreground">{usd(detalle.total)}</span>
                  </div>
                </div>
                {detalle.comprobante && (
                  <div>
                    <div className="text-xs font-medium mb-1">Comprobante de transferencia:</div>
                    <img src={detalle.comprobante} alt="comprobante" className="max-h-56 rounded border" />
                  </div>
                )}
                <div className="text-xs space-y-0.5 border-t pt-2">
                  {detalle.eventos.map((e, i) => (
                    <div key={i} className="flex justify-between gap-2">
                      <span>{e.evento}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{new Date(e.fecha).toLocaleString(LOCALE, { hour12: false })}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="font-semibold">Acciones</div>
                {detalle.estado === 'pendiente_pago' && (
                  <Button size="sm" className="w-full" onClick={() => void avanzar(detalle.id, 'aprobar_pago')}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar pago (factura SRI + descuenta stock)
                  </Button>
                )}
                {detalle.estado === 'pagado' && (
                  <Button size="sm" className="w-full" variant="outline" onClick={() => void avanzar(detalle.id, 'preparacion')}>
                    Pasar a preparación
                  </Button>
                )}
                {(detalle.estado === 'pagado' || detalle.estado === 'en_preparacion') && (
                  <div className="flex gap-2">
                    <input className={`${fieldCls} flex-1`} placeholder="Guía del courier (opcional)"
                           value={guia} onChange={(e) => setGuia(e.target.value)} />
                    <Button size="sm" onClick={() => void avanzar(detalle.id, 'enviado', { guia })}>
                      <Truck className="w-4 h-4 mr-1" /> Despachar
                    </Button>
                  </div>
                )}
                {(detalle.estado === 'enviado' || detalle.estado === 'en_preparacion') && (
                  <Button size="sm" className="w-full" variant="outline" onClick={() => void avanzar(detalle.id, 'entregado')}>
                    Marcar ENTREGADO
                  </Button>
                )}
                {['pendiente_pago', 'pagado', 'en_preparacion'].includes(detalle.estado) && !detalle.ticket_id && (
                  <Button size="sm" className="w-full" variant="ghost"
                          onClick={async () => {
                            const v = await dlg.pedir('Cancelar pedido', [{ key: 'motivo', label: 'Motivo de cancelación' }]);
                            if (v) void avanzar(detalle.id, 'cancelar', { motivo: v.motivo });
                          }}>
                    Cancelar pedido
                  </Button>
                )}
                {detalle.ticket_id && ['pagado', 'en_preparacion', 'enviado', 'entregado'].includes(detalle.estado) && (
                  <Button size="sm" className="w-full" variant="ghost"
                          onClick={async () => { if (await dlg.confirmar('¿Marcar REEMBOLSADO?', { detalle: 'La NC se emite desde Financiero.' })) void avanzar(detalle.id, 'reembolsado'); }}>
                    Marcar reembolsado (NC emitida)
                  </Button>
                )}
                <p className="text-xs text-muted-foreground border-t pt-2">
                  Contraentrega se factura al DESPACHAR (la factura viaja con el paquete). Un pedido facturado no
                  se cancela: se devuelve con nota de crédito desde Financiero.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
interface CarritoT {
  id: string; nombre: string | null; email: string | null; telefono: string | null;
  items: { titulo: string; cantidad: number; precio: number }[]; updated_at: string; total: number;
}
export function CarritosWeb() {
  const { data: carritos = [] } = useQuery<CarritoT[]>({
    queryKey: ['ecom-carritos'],
    queryFn: () => ecomRpc<CarritoT[]>('ecom_carritos', { _dias: 14 }),
    staleTime: 0, refetchOnMount: 'always',
  });
  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><ShoppingBasket className="w-5 h-5" /> Carritos abandonados</h1>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Últimos 14 días (con contacto capturado, sin compra)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {carritos.map((c) => (
            <div key={c.id} className="border rounded-md px-3 py-2 flex items-center gap-3 text-sm flex-wrap">
              <div className="flex-1 min-w-40">
                <div className="font-medium">{c.nombre ?? '(sin nombre)'}</div>
                <div className="text-xs text-muted-foreground">{c.email ?? ''} {c.telefono ? `· ${c.telefono}` : ''}</div>
              </div>
              <div className="text-xs flex-1">{c.items.map((i) => `${i.cantidad}× ${i.titulo}`).join(' · ')}</div>
              <span className="font-mono font-semibold">{usd(c.total)}</span>
              <span className="text-xs text-muted-foreground">{new Date(c.updated_at).toLocaleString(LOCALE, { hour12: false })}</span>
              {c.telefono && (
                <a className="text-xs text-primary underline" target="_blank" rel="noopener noreferrer"
                   href={`https://wa.me/${c.telefono.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${c.nombre ?? ''}, vimos que dejaste tu carrito con ${c.items.length} producto(s). ¿Te ayudamos a completar la compra?`)}`}>
                  Recuperar por WhatsApp
                </a>
              )}
            </div>
          ))}
          {!carritos.length && <p className="py-6 text-center text-sm text-muted-foreground">Sin carritos abandonados con contacto. 🎉</p>}
          <p className="text-xs text-muted-foreground border-t pt-2">
            La recuperación automática por email/WhatsApp llega en Fase 2; mientras tanto el enlace abre WhatsApp
            con el mensaje listo.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
